import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useMemo, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";

import { PlatformIcon } from "@/components/competitors/PlatformIcon";
import { SELECT_CLASS } from "@/components/market-intel/FormDialog";
import { isIsoDay } from "@/components/market-intel/dates";
import { SegmentedControl } from "@/components/atomic-crm/tasks/SegmentedControl";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

import {
  dayOf,
  gridOf,
  groupPiecesByDay,
  listGroups,
  parseDay,
  pieceDate,
  scheduledInputOf,
  shiftCursor,
  visibleRange,
  weekDaysOf,
  type CalendarView as View,
} from "./calendar";
import { STATUS_COLOR } from "./constants";
import { useMarketing } from "./context";
import { findById } from "./hooks";
import { PieceRow } from "./PieceRow";
import { filterPieces, isOverdue } from "./pipeline";
import type { ContentItem } from "./types";

const VIEW_PARAM: Record<View, string> = {
  month: "mes",
  week: "semana",
  list: "lista",
};

const viewOfParam = (value: string | null): View =>
  (Object.entries(VIEW_PARAM).find(([, param]) => param === value)?.[0] as
    View | undefined) ?? "month";

const MAX_CHIPS = 3;

/**
 * Marketing > Calendario: pieces by planned (or publication) day, as a month,
 * a week or a list. Tap a day to plan a piece on it, a piece to open it.
 */
export const CalendarView = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const { data, newPiece } = useMarketing();
  const view = viewOfParam(params.get("vista"));
  const today = dayOf(new Date());
  const cursorDay = params.get("fecha");
  const cursor = parseDay(isIsoDay(cursorDay) ? cursorDay : today);
  const channel = params.get("canal") ?? "";
  const [selected, setSelected] = useState(today);

  const setParam = (changes: Record<string, string | null>) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes))
          if (value) next.set(key, value);
          else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const pieces = useMemo(
    () => filterPieces(data.items, { channel }),
    [data.items, channel],
  );
  const byDay = useMemo(() => groupPiecesByDay(pieces), [pieces]);
  const range = visibleRange(view, cursor);
  const visibleCount = listGroups(byDay, range.start, range.end).reduce(
    (sum, group) => sum + group.items.length,
    0,
  );

  const move = (step: 1 | -1) => {
    const next = dayOf(shiftCursor(view, cursor, step));
    setParam({ fecha: next });
    setSelected(next);
  };
  const goToday = () => {
    setParam({ fecha: null });
    setSelected(today);
  };

  const title =
    view === "week"
      ? weekTitle(range.start, range.end, locale)
      : capitalize(
          formatDate(cursor, locale, { month: "long", year: "numeric" }),
        );
  const viewOptions = (["month", "week", "list"] as const).map((value) => ({
    value,
    label: translate(`marketing.calendar.${value}`),
  }));
  const addOn = (day: string) =>
    newPiece({ scheduled_at: scheduledInputOf(day), channel_id: channel });

  return (
    <section className="space-y-3 md:space-y-4" aria-labelledby="mk-cal-title">
      <h2 className="sr-only">{translate("marketing.calendar.heading")}</h2>
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl<View>
          value={view}
          options={viewOptions}
          onChange={(next) =>
            setParam({ vista: next === "month" ? null : VIEW_PARAM[next] })
          }
          label={translate("marketing.calendar.view_label")}
          glass={isMobile}
          size="sm"
          className="w-full sm:w-64"
        />
        <div className="flex min-w-0 flex-1 items-center gap-1 sm:flex-none">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="mk-nav-btn"
            onClick={() => move(-1)}
            aria-label={translate("marketing.calendar.previous")}
          >
            <ChevronLeft aria-hidden className="size-5" />
          </Button>
          <p
            id="mk-cal-title"
            aria-live="polite"
            className="min-w-0 flex-1 truncate text-center text-base font-semibold tracking-tight sm:w-52 sm:flex-none"
          >
            {title}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="mk-nav-btn"
            onClick={() => move(1)}
            aria-label={translate("marketing.calendar.next")}
          >
            <ChevronRight aria-hidden className="size-5" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-1 h-9 rounded-full px-3"
            onClick={goToday}
          >
            {translate("marketing.calendar.today")}
          </Button>
        </div>
        <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
          <label htmlFor="mk-cal-channel" className="sr-only">
            {translate("marketing.common.filter_channel")}
          </label>
          <select
            id="mk-cal-channel"
            className={cn(
              SELECT_CLASS,
              "h-9 min-w-0 flex-1 rounded-full sm:w-48",
            )}
            value={channel}
            onChange={(event) =>
              setParam({ canal: event.target.value || null })
            }
          >
            <option value="">
              {translate("marketing.common.all_channels")}
            </option>
            {data.channels.map((entry) => (
              <option key={entry.id} value={String(entry.id)}>
                {entry.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            className="btn-save h-9 shrink-0 rounded-full px-4"
            onClick={() =>
              addOn(view === "month" && isMobile ? selected : today)
            }
          >
            <Plus aria-hidden className="size-4" />
            <span className="max-sm:sr-only">
              {translate("marketing.common.new_piece")}
            </span>
          </Button>
        </div>
      </div>
      <p className="sr-only" aria-live="polite">
        {translate("marketing.calendar.pieces", { smart_count: visibleCount })}
      </p>
      {view === "list" ? (
        <ListView byDay={byDay} start={range.start} end={range.end} />
      ) : view === "week" && isMobile ? (
        <WeekList byDay={byDay} start={range.start} onAdd={addOn} />
      ) : (
        <MonthGrid
          view={view}
          cursor={cursor}
          byDay={byDay}
          today={today}
          compact={isMobile}
          selected={selected}
          onSelect={setSelected}
          onAdd={addOn}
        />
      )}
      {isMobile && view === "month" ? (
        <DayList
          day={selected}
          items={byDay.get(selected) ?? []}
          onAdd={() => addOn(selected)}
        />
      ) : null}
    </section>
  );
};

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const weekTitle = (start: string, end: string, locale: string): string => {
  const from = parseDay(start);
  const to = parseDay(end);
  const sameMonth = from.getMonth() === to.getMonth();
  return `${formatDate(from, locale, sameMonth ? { day: "numeric" } : { day: "numeric", month: "short" })} – ${formatDate(to, locale, { day: "numeric", month: "short", year: "numeric" })}`;
};

const weekdayNames = (locale: string, start: string): string[] =>
  weekDaysOf(start).map((day) =>
    formatDate(parseDay(day), locale, { weekday: "short" }).replace(".", ""),
  );

interface MonthGridProps {
  view: View;
  cursor: Date;
  byDay: Map<string, ContentItem[]>;
  today: string;
  compact: boolean;
  selected: string;
  onSelect: (day: string) => void;
  onAdd: (day: string) => void;
}

const MonthGrid = ({
  view,
  cursor,
  byDay,
  today,
  compact,
  selected,
  onSelect,
  onAdd,
}: MonthGridProps) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const weeks = gridOf(view, cursor);
  const names = weekdayNames(locale, weeks[0][0].key);
  const isWeek = view === "week";

  return (
    <div
      className={cn(
        "mk-cal mi-card",
        compact && "mk-cal--compact",
        isWeek && "mk-cal--week",
      )}
      role="grid"
      aria-label={translate("marketing.calendar.heading")}
    >
      <div className="mk-cal__head" role="row">
        {names.map((name) => (
          <span key={name} role="columnheader" className="mk-cal__weekday">
            {name}
          </span>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0].key} className="mk-cal__row" role="row">
          {week.map((day) => {
            const items = byDay.get(day.key) ?? [];
            const dayLabel = formatDate(day.date, locale, {
              weekday: "long",
              day: "numeric",
              month: "long",
            });
            const common = {
              "data-today": day.key === today ? "true" : undefined,
              "data-out": day.inMonth ? undefined : "true",
            };
            if (compact)
              return (
                <button
                  key={day.key}
                  type="button"
                  role="gridcell"
                  {...common}
                  data-selected={day.key === selected ? "true" : undefined}
                  aria-selected={day.key === selected}
                  aria-label={`${dayLabel}: ${translate("marketing.calendar.pieces", { smart_count: items.length })}`}
                  className="mk-cal__cell"
                  onClick={() => onSelect(day.key)}
                >
                  <span className="mk-cal__num">{day.date.getDate()}</span>
                  <span className="mk-cal__dots" aria-hidden>
                    {items.slice(0, 3).map((item) => (
                      <span
                        key={item.id}
                        style={{ background: STATUS_COLOR[item.status] }}
                      />
                    ))}
                  </span>
                </button>
              );
            const shown = isWeek ? items : items.slice(0, MAX_CHIPS);
            return (
              <div
                key={day.key}
                role="gridcell"
                {...common}
                className="mk-cal__cell"
              >
                <button
                  type="button"
                  className="mk-cal__add"
                  onClick={() => onAdd(day.key)}
                  aria-label={translate("marketing.calendar.add_on_day", {
                    day: dayLabel,
                  })}
                >
                  <span className="mk-cal__num">{day.date.getDate()}</span>
                  <Plus aria-hidden className="mk-cal__plus size-3.5" />
                </button>
                <div className="mk-cal__chips">
                  {shown.map((item) => (
                    <PieceChip key={item.id} piece={item} withTime={isWeek} />
                  ))}
                  {items.length > shown.length ? (
                    <span className="mk-cal__more">
                      {translate("marketing.calendar.more", {
                        count: items.length - shown.length,
                      })}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const PieceChip = ({
  piece,
  withTime,
}: {
  piece: ContentItem;
  withTime?: boolean;
}) => {
  const [locale = "es"] = useLocaleState();
  const { data, openPiece } = useMarketing();
  const channel = findById(data.channels, piece.channel_id);
  const date = pieceDate(piece);
  return (
    <button
      type="button"
      onClick={() => openPiece(piece.id)}
      className="mk-chip"
      data-overdue={isOverdue(piece, new Date()) ? "true" : undefined}
      style={{ "--mk-status": STATUS_COLOR[piece.status] } as CSSProperties}
      title={piece.title}
    >
      {channel ? (
        <PlatformIcon platform={channel.platform} className="size-3.5" />
      ) : null}
      {withTime && date ? (
        <span className="mk-chip__time">
          {formatDate(date, locale, { hour: "2-digit", minute: "2-digit" })}
        </span>
      ) : null}
      <span className="truncate">{piece.title}</span>
    </button>
  );
};

const DayList = ({
  day,
  items,
  onAdd,
}: {
  day: string;
  items: ContentItem[];
  onAdd: () => void;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const label = capitalize(
    formatDate(parseDay(day), locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }),
  );
  return (
    <section className="mi-card p-4" aria-live="polite">
      <header className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-[17px] font-semibold tracking-tight">{label}</h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="mk-nav-btn text-primary"
          onClick={onAdd}
          aria-label={translate("marketing.calendar.add_on_day", {
            day: label,
          })}
        >
          <Plus aria-hidden className="size-5" />
        </Button>
      </header>
      {items.length ? (
        <ul className="-mx-1 flex flex-col">
          {items.map((item, index) => (
            <PieceRow
              key={item.id}
              piece={item}
              showDate={false}
              index={index}
            />
          ))}
        </ul>
      ) : (
        <p className="py-2 text-sm text-muted-foreground">
          {translate("marketing.calendar.empty_day")}
        </p>
      )}
    </section>
  );
};

/** The week on a phone: seven day sections stacked. */
const WeekList = ({
  byDay,
  start,
  onAdd,
}: {
  byDay: Map<string, ContentItem[]>;
  start: string;
  onAdd: (day: string) => void;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const today = dayOf(new Date());
  const days = weekDaysOf(start);
  return (
    <div className="flex flex-col gap-3">
      {days.map((day) => {
        const items = byDay.get(day) ?? [];
        const label = capitalize(
          formatDate(parseDay(day), locale, {
            weekday: "long",
            day: "numeric",
            month: "short",
          }),
        );
        return (
          <section
            key={day}
            className="mi-card px-4 py-3"
            data-today={day === today ? "true" : undefined}
          >
            <header className="flex items-center justify-between gap-2">
              <h3
                className={cn(
                  "text-sm font-semibold",
                  day === today && "text-primary dark:text-[var(--mcal-today)]",
                )}
              >
                {label}
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mk-nav-btn size-8 text-primary"
                onClick={() => onAdd(day)}
                aria-label={translate("marketing.calendar.add_on_day", {
                  day: label,
                })}
              >
                <Plus aria-hidden className="size-4" />
              </Button>
            </header>
            {items.length ? (
              <ul className="-mx-1 flex flex-col">
                {items.map((item) => (
                  <PieceRow key={item.id} piece={item} showDate={false} />
                ))}
              </ul>
            ) : (
              <p className="pb-1 text-xs text-muted-foreground">
                {translate("marketing.calendar.empty_day")}
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
};

const ListView = ({
  byDay,
  start,
  end,
}: {
  byDay: Map<string, ContentItem[]>;
  start: string;
  end: string;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const groups = listGroups(byDay, start, end);
  if (!groups.length)
    return (
      <p className="mi-card p-6 text-center text-sm text-muted-foreground">
        {translate("marketing.calendar.empty_list")}
      </p>
    );
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <section key={group.day} className="mi-card px-4 py-3">
          <h3 className="text-sm font-semibold">
            {capitalize(
              formatDate(parseDay(group.day), locale, {
                weekday: "long",
                day: "numeric",
                month: "long",
              }),
            )}
          </h3>
          <ul className="-mx-1 flex flex-col">
            {group.items.map((item, index) => (
              <PieceRow
                key={item.id}
                piece={item}
                showDate={false}
                index={index}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};
